import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const ServicePage = ({ page }) => (
  <div data-testid={`page-service-${page.service.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    {/* HERO */}
    <section className="py-16 md:py-24 border-b border-[#24211D]/10">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className={page.image ? "lg:col-span-7" : "lg:col-span-12 max-w-3xl"}>
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">{page.eyebrow}</span>
          <h1 className="text-[clamp(36px,4.5vw,72px)] leading-none mb-6">
            {page.titleLines.map((line, i) => (
              <span key={i} className="ln"><span>{line}</span></span>
            ))}
          </h1>
          <p className="text-base text-[#24211D]/80 max-w-lg mb-8 leading-relaxed">{page.intro}</p>
          <Link
            to={`/kontakt?service=${encodeURIComponent(page.service)}#termin`}
            data-testid="service-cta-btn"
            className="bg-[#B08D57] hover:bg-[#967442] text-[#FBF8F3] px-8 py-4 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300 inline-block"
          >
            {page.ctaLabel}
          </Link>
        </div>
        {page.image && (
          <div className="lg:col-span-5">
            <figure className="aspect-[3/4] relative border border-[#24211D]/10 overflow-hidden nes-wipe">
              <img src={page.image} alt={page.imageAlt} className="absolute inset-0 w-full h-full object-cover" />
              <figcaption className="absolute bottom-4 left-4 text-xs uppercase tracking-[0.14em] text-[#24211D]/85 bg-[#FBF8F3]/90 px-3 py-2 font-sans">{page.imageCaption}</figcaption>
            </figure>
          </div>
        )}
      </div>
    </section>

    {/* FÜR WEN + FAKTEN */}
    <section className="py-16 border-b border-[#24211D]/10">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Gut zu wissen</span>
          <h2 className="text-3xl md:text-4xl mb-5">{page.factsTitle}</h2>
          <p className="text-sm text-[#24211D]/80 leading-relaxed">{page.factsText}</p>
        </div>
        <div className="grid grid-cols-2 gap-6 text-xs font-sans self-center">
          {page.facts.map((f) => (
            <div key={f.t} className="border-l border-[#B08D57] pl-4">
              <span className="font-bold block text-[#24211D]">{f.t}</span>
              <span className="text-[#24211D]/70">{f.d}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ABLAUF */}
    <section className="bg-[#24211D] text-[#FBF8F3] py-20">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Der Ablauf</span>
          <h2 className="text-3xl md:text-4xl">{page.stepsTitle}</h2>
          <p className="text-xs text-[#FBF8F3]/70 font-sans mt-3">{page.stepsNote}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {page.steps.map((s) => (
            <div key={s.nr} className="border-t border-[#B08D57]/40 pt-6" data-testid={`service-step-${s.nr}`}>
              <span className="text-xs font-display text-[#B08D57] block mb-2">{s.nr}</span>
              <h3 className="text-xl mb-2">{s.title}</h3>
              <p className="text-xs text-[#FBF8F3]/70 leading-relaxed font-sans">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* TIPPS */}
    <section className="py-20 border-b border-[#24211D]/10">
      <div className="max-w-3xl mx-auto px-6">
        <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Gut zu wissen</span>
        <h2 className="text-3xl md:text-4xl mb-8">{page.tipsTitle}</h2>
        <ul className="space-y-3">
          {page.tips.map((t) => (
            <li key={t} className="text-sm text-[#24211D]/80 border-b border-[#24211D]/10 pb-3 flex gap-3">
              <span className="text-[#B08D57]">—</span>{t}
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-20 border-b border-[#24211D]/10">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.2em] text-[#B08D57] block mb-2 font-sans">Häufige Fragen</span>
          <h2 className="text-3xl md:text-4xl">FAQ</h2>
        </div>
        <Accordion type="single" collapsible className="border-t border-[#24211D]/10" data-testid="service-faq">
          {page.faq.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#24211D]/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg hover:no-underline hover:text-[#B08D57] transition-colors duration-200">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#24211D]/75 leading-relaxed font-sans">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-[#B08D57] text-[#FBF8F3] py-14">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-2xl md:text-4xl mb-3">{page.ctaTitle}</h2>
        <p className="text-sm text-[#FBF8F3]/85 font-sans mb-8">{page.ctaText}</p>
        <Link
          to={`/kontakt?service=${encodeURIComponent(page.service)}#termin`}
          data-testid="service-footer-cta"
          className="bg-[#FBF8F3] text-[#24211D] hover:bg-[#FFFFFF] px-10 py-4 text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-300 inline-block"
        >
          {page.ctaLabel}
        </Link>
      </div>
    </section>
  </div>
);
